
import { jobQueueStatus as api, JobQueueStatusInfo, JobState } from 'st-app/api'
import { Accessor, Setter, createContext, createEffect, createMemo, createRoot, createSignal, useContext } from 'solid-js'
import { toast } from 'common'

export class JobQueueStatusContext {
    activeJobs: Accessor<JobQueueStatusInfo[]>
    completedJobs: Accessor<JobQueueStatusInfo[]>
    errorJobs: Accessor<JobQueueStatusInfo[]>

    iconBadgeCount: Accessor<number>
    iconBadgeColor: Accessor<string>

    isProcessing: Accessor<boolean>
    showBadge: Accessor<boolean>
    private jobs: Accessor<JobQueueStatusInfo[]>; private setJobs: Setter<JobQueueStatusInfo[]> 
    historicalJobs: Accessor<JobQueueStatusInfo[]>; private setHistoricalJobs: Setter<JobQueueStatusInfo[]>
    loadingHistoricalJobs: Accessor<boolean>;  setLoadingHistoricalJobs: Setter<boolean>
    
    singularJobs: Accessor<JobQueueStatusInfo[]>;
    recurringJobs: Accessor<JobQueueStatusInfo[]>;

    canAccessUserViewer: Accessor<boolean>; private setCanAccessUserViewer: Setter<boolean>
    canAccessAdminViewer: Accessor<boolean>; private setCanAccessAdminViewer: Setter<boolean>

    selectedJobIds: Accessor<string[]>; setSelectedJobIds: Setter<string[]>

    retrievedActionsJobIds: Accessor<string[]>; setRetrievedActionsJobIds: Setter<string[]>

    private timeToWait: number = 1000;
    private refreshTimeout?: number;
    private historyExhausted = false;

    constructor() {
        [this.jobs, this.setJobs] = createSignal<JobQueueStatusInfo[]>([]);
        [this.historicalJobs, this.setHistoricalJobs] = createSignal<JobQueueStatusInfo[]>([]);
        [this.loadingHistoricalJobs, this.setLoadingHistoricalJobs] = createSignal<boolean>(false);
        [this.retrievedActionsJobIds, this.setRetrievedActionsJobIds] = createSignal<string[]>([]);

        this.activeJobs = createMemo(() => this.jobs().filter(j => this.isActive(j)));
        this.completedJobs = createMemo(() => this.jobs().filter(j => j.state === JobState.Complete));
        this.errorJobs = createMemo(() => this.jobs().filter(j => j.state === JobState.Error));
        this.recurringJobs = createMemo(() => this.jobs().filter(j => j.recurring === true));

        this.iconBadgeCount = createMemo(() => {
            if (this.errorJobs().length > 0) return this.errorJobs().length;
            if (this.activeJobs().length > 0) return this.activeJobs().length;
            if (this.completedJobs().length > 0) return this.completedJobs().length;
            return 0;
        });

        this.showBadge = createMemo(() => this.iconBadgeCount() > 0);

        this.iconBadgeColor = createMemo(() => {
            if (this.errorJobs().length > 0) return 'error';
            if (this.activeJobs().length > 0) return 'success';
            if (this.completedJobs().length > 0) return 'info';
            return '';
        })

        this.isProcessing = createMemo(() => this.activeJobs().length > 0);
        this.singularJobs = createMemo(() => this.jobs().filter(j => j.recurring === false));

        [this.canAccessUserViewer, this.setCanAccessUserViewer] = createSignal<boolean>(false);
        [this.canAccessAdminViewer, this.setCanAccessAdminViewer] = createSignal<boolean>(false);

        [this.selectedJobIds, this.setSelectedJobIds] = createSignal<string[]>([]);

        createEffect(() => {
            const recurringJobIds = this.recurringJobs().map(j => j.processQueueGU!);
            this.setSelectedJobIds(ids => ids.filter(id => recurringJobIds.includes(id)));
        })
    }

    toggleSelectedJob(job: JobQueueStatusInfo, selected: boolean) {
        const ids = this.selectedJobIds();
        if (selected) {
            this.setSelectedJobIds([...ids, job.processQueueGU!]);
        } else {
            this.setSelectedJobIds(ids.filter(id => id !== job.processQueueGU));
        }
    }

    async abort(job: JobQueueStatusInfo) {
        try {
            this.moveToHistorical(job);
            await api.AbortJobs([job.processQueueGU!])
        } catch (e) {
            console.error(e);
        } finally {
            this.refreshStatusList();
        }
    }

    moveToHistorical(job: JobQueueStatusInfo) {
        if (this.historicalJobs().some(j => j.processQueueGU === job.processQueueGU)) 
            return;

        this.setJobs(this.jobs().filter(j => j.processQueueGU !== job.processQueueGU));
        this.setHistoricalJobs([job, ...this.historicalJobs()]);
    }

    async dismiss(job: JobQueueStatusInfo) {
        if (import.meta.env.DEV) {
            window.alert('Dismissing job not implemented in dev mode');
            return;
        }

        this.moveToHistorical(job);

        window.ST.SendServerEvent(null, 'JobQueue_Remove_Job', {
            '@FOCUS_KEY': window.ST.RevFocusKey,
            '@JOB_GUID': job.processQueueGU,
        }).then(() => {
            this.refreshStatusList();
            this.refreshHistoricalJobs();
        })

    }

    async dismissAll() {
        if (import.meta.env.DEV) {
            window.alert('Dismissing all jobs not implemented in dev mode');
            return;
        }

        const jobGUs = this.completedJobs().map(j => j.processQueueGU!);
        try {
            window.ST.SendServerEvent(null, 'JobQueue_Dismiss_All', {
                '@FOCUS_KEY': window.ST.RevFocusKey,
                '@JOB_GUID': jobGUs.join(',')
            });
            this.setJobs(jobs => jobs.filter(j => j.state !== JobState.Complete));
            this.setHistoricalJobs([...this.historicalJobs(), ...this.completedJobs()]);

            this.refreshStatusList();
        } catch {
            toast.notify('error', 'An error occurred while dismissing all jobs');
        }
    }

    private isActive(job: JobQueueStatusInfo) {
        switch (job.state) {
            case JobState.InProgress:
                return true;
            case JobState.Hold:
            case JobState.Waiting:
                if (job.recurring || !job.submitDT) {
                    return false;
                } else {
                    return new Date(job.submitDT).getTime() < Date.now()
                }
            default:
                return false;
        }
    }

    async refreshStatusList() {
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout!);
        }

        const listResponse = await api.GetStatusList(true);

        this.setCanAccessUserViewer(listResponse.userViewerAccess);
        this.setCanAccessAdminViewer(listResponse.adminViewerAccess);

        const currentJobs = this.jobs();
        const orphanedJobs = currentJobs.filter(j => !listResponse.jobs.some(l => l.processQueueGU === j.processQueueGU));
        if (orphanedJobs.length > 0) {
            this.refreshHistoricalJobs();
        }

        const jobsFromServer = listResponse.jobs.map(job => {
            const existing = currentJobs.find(c => c.processQueueGU === job.processQueueGU);
            return existing ? { ...existing, ...job } : job;
        })

        let hasChanged = orphanedJobs.length > 0;
        for (const job of jobsFromServer) {
            const existing = currentJobs.find(c => c.processQueueGU === job.processQueueGU);
            if (!existing) {
                hasChanged = true;
                break;
            }

            if (existing.state !== job.state) {
                hasChanged = true;
                break;
            }

            if (existing.userJobStatus !== job.userJobStatus) {
                hasChanged = true;
                break;
            }
        }

        if (hasChanged) {
            this.timeToWait = this.activeJobs().length > 0 ? 2000 : 4000;
        } else {
            this.timeToWait = Math.min(this.timeToWait * 2, 32000);
        }

        this.setJobs(jobsFromServer.toSorted((a, b) => new Date(b.submitDT as string).getTime() - new Date(a.submitDT as string).getTime()));

        const completedJobs = this.completedJobs();
        const jobsToCheckForActions = completedJobs.filter(j => !this.retrievedActionsJobIds().includes(j.processQueueGU!));
        for (const jobToCheckForAction of jobsToCheckForActions) {
            this.checkForActionsLegacy(jobToCheckForAction.processQueueGU!);
        }

        this.refreshTimeout = window.setTimeout(() => this.refreshStatusList(), this.timeToWait);
    }

    async refreshHistoricalJobs() {
        this.setLoadingHistoricalJobs(true);
        try {
            const before = '';
            const after = this.historicalJobs()[0]?.completionDT ?? '';
            const jobs = await api.GetHistoricalJobs(before, after);
            this.setHistoricalJobs(jobs);
        } catch(e) {
            console.error(e);
        }
        this.setLoadingHistoricalJobs(false);
    }

    async loadMoreJobs() {
        if (this.historyExhausted) return;

        this.setLoadingHistoricalJobs(true);
        try {
            const before = this.historicalJobs().at(-1)?.completionDT ?? '';
            const after = '';
            const jobs = await api.GetHistoricalJobs(before, after);
            if (jobs.length === 0) {
                this.historyExhausted = true;
            }

            this.setHistoricalJobs([...this.historicalJobs(), ...jobs]);
        } catch (e) {
            console.error(e);
        }
        this.setLoadingHistoricalJobs(false);
    }

    async checkForActionsLegacy(jobId: string) {
        this.setRetrievedActionsJobIds([...this.retrievedActionsJobIds(), jobId]);
        if (window.ST && window.ST.SendServerEvent) {
            const xml = {
                '@FOCUS_KEY': window.ST.RevFocusKey,
                '@GUID': jobId,
            }

            window.ST.SendServerEvent(null, 'JobQueue_Get_Status', xml, null);
        }
    }

    dispose() {
        if (this.refreshTimeout) {
            window.clearTimeout(this.refreshTimeout);
        }
    }
}

let context: JobQueueStatusContext;
createRoot(() => {
    context = new JobQueueStatusContext();
})

export const requireJobQueueStatusContext = () => {
    return context!;
}
